<template>
  <div>
    <!--Display States-->
    <!--Text Field-->
    <div
      class="inline-wrapper inline-textfield-wrapper"
      v-if="!editing && componentType === COMPONENT_TYPE_TEXTFIELD"
      @click="enterEditMode"
    >
      <span
        class="inline-textfield-formatted"
        v-html="valModel ? renderFormatted(valModel) : placeholder"
      ></span>
      <v-icon size="small" style="top: -3px; left: 3px; display: inline">
        mdi-pencil
      </v-icon>
    </div>
    <!--Text Area-->
    <div
      class="inline-wrapper inline-textarea-wrapper"
      v-if="!editing && componentType === COMPONENT_TYPE_TEXTAREA"
      @click="enterEditMode"
    >
      <template v-if="valModel">
        <div
          class="inline-textarea-formatted"
          v-html="renderFormatted(valModel)"
        ></div>
        <v-btn class="mt-4">
          <v-icon class="mr-1"> mdi-pencil</v-icon>
          Edit
        </v-btn>
      </template>
      <template v-else>
        <div class="pa-10 text-center" v-html="placeholder"></div>
      </template>
    </div>
    <!-- Select -->
    <div
      class="inline-wrapper inline-textfield-wrapper"
      v-if="!editing && componentType === COMPONENT_TYPE_SELECT"
      @click="enterEditMode"
    >
      <span
        class="inline-textfield-formatted"
        v-html="valModel ? renderFormatted(valModel) : placeholder"
      ></span>
      <v-icon size="small" style="top: -3px; left: 3px; display: inline">
        mdi-pencil
      </v-icon>
    </div>
    <!-- Switch -->
    <div
      class="inline-wrapper inline-textfield-wrapper"
      v-if="!editing && componentType === COMPONENT_TYPE_SWITCH"
      @click="enterEditMode"
    >
      <span
        class="inline-textfield-formatted"
        v-html="valModel ? 'Yes' : 'No'"
      ></span>
      <v-icon size="small" style="top: -3px; left: 3px; display: inline">
        mdi-pencil
      </v-icon>
    </div>
    <!--Phone-->
    <div
      class="inline-wrapper inline-textfield-wrapper"
      v-if="!editing && componentType === COMPONENT_TYPE_PHONE"
      @click="enterEditMode"
    >
      <span
        class="inline-textfield-formatted"
        v-html="valModel ? renderFormatted(valModel) : placeholder"
      ></span>
      <v-icon size="small" style="top: -3px; left: 3px; display: inline">
        mdi-pencil
      </v-icon>
    </div>
    <!--Editing States-->
    <v-form v-model="valid" ref="editingState" v-if="editing">
      <!--Textfield-->
      <div v-if="editing && componentType === COMPONENT_TYPE_TEXTFIELD">
        <v-text-field
          :autofocus="true"
          v-model="valModel"
          class="mt-0 mb-0 pt-0 pb-0"
          :rules="rules"
          @blur="persistWrapper"
        >
          <template slot="append">
            <v-progress-circular
              v-if="loading"
              width="2"
              size="22"
              indeterminate
            ></v-progress-circular>
            <template v-else>
              <v-icon @click="cancelEditMode">mdi-cancel</v-icon>
              <v-icon @click="persistWrapper" color="green">mdi-check</v-icon>
            </template>
          </template>
        </v-text-field>
      </div>
      <!-- TextArea-->
      <div v-if="editing && componentType === COMPONENT_TYPE_TEXTAREA">
        <v-textarea :autofocus="true" v-model="valModel" :rules="rules"></v-textarea>
        <v-btn @click="cancelEditMode" class="mr-4">Cancel</v-btn>
        <v-btn :loading="loading" @click="persistWrapper" color="primary">
          Save
        </v-btn>
      </div>
      <!-- Select -->
      <div v-if="editing && componentType === COMPONENT_TYPE_SELECT">
        <v-select :items="selectOptions" :autofocus="true" v-model="valModel" :rules="rules"></v-select>
        <v-btn @click="cancelEditMode" class="mr-4">Cancel</v-btn>
        <v-btn :loading="loading" @click="persistWrapper" color="primary">
          Save
        </v-btn>
      </div>
      <!-- Switch -->
      <div v-if="editing && componentType === COMPONENT_TYPE_SWITCH" style="text-align: right;">
        <v-switch class="switch-component" v-model="valModel" :label="valModel ? 'Yes' : 'No'" :rules="rules">
          <template slot="append">
            <v-progress-circular
              v-if="loading"
              width="2"
              size="22"
              indeterminate
              reverse
            ></v-progress-circular>
            <template v-else>
              <v-icon @click="cancelEditMode">mdi-cancel</v-icon>
              <v-icon @click="persistWrapper" color="green">mdi-check</v-icon>
            </template>
          </template>
        </v-switch>
      </div>
      <!-- Phone -->
      <div v-if="editing && componentType === COMPONENT_TYPE_PHONE" style="text-align: right;">
        <number-field
          :options="{ type: `phone` }"
          :autofocus="true"
          v-model="valModel"
          @blur="persistWrapper"
          lazy-validation
          class="mt-0 mb-0 pt-0"
          :rules="rules"
        >
          <template slot="append">
            <v-progress-circular
              v-if="loading"
              width="2"
              size="22"
              indeterminate
            ></v-progress-circular>
            <template v-else>
              <v-icon @click="cancelEditMode">mdi-cancel</v-icon>
              <v-icon @click="persistWrapper" color="green">mdi-check</v-icon>
            </template>
          </template>
        </number-field>
      </div>
    </v-form>
    <!--Raw Value-->
    <!--<pre>Raw Value: {{ val }}</pre>-->
  </div>
</template>

<script>
import NumberField from '@/components/NumberField'

const COMPONENT_TYPE_TEXTFIELD = `textfield`
const COMPONENT_TYPE_TEXTAREA = `textarea`
const COMPONENT_TYPE_SELECT = `select`
const COMPONENT_TYPE_SWITCH = `switch`
const COMPONENT_TYPE_PHONE = `phone`
const COMPONENT_TYPES = [COMPONENT_TYPE_TEXTFIELD, COMPONENT_TYPE_TEXTAREA, COMPONENT_TYPE_SELECT, COMPONENT_TYPE_SWITCH]

export default {
  components: {
    NumberField,
  },
  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
    selectOptions: {
      type: Array,
    },
    placeholder: {
      type: String,
    },
    val: {
      type: [String, Object, Boolean],
    },
    componentType: {
      type: String,
    },
    rules: {
      type: Array,
      default(val) {
        return val || []
      },
    },
    renderFormatted: {
      type: Function,
      default(val) {
        return `<div>${val}</div>`
      },
    },
    renderEditing: {
      type: Function,
      default(val) {
        return val
      },
    },
    persist: {
      type: Function,
    },
  },
  data() {
    return {
      valid: null,
      revertValModel: null,
      valModel: null,
      editing: false,
      loading: false,
      COMPONENT_TYPE_TEXTAREA,
      COMPONENT_TYPE_TEXTFIELD,
      COMPONENT_TYPE_SELECT,
      COMPONENT_TYPE_SWITCH,
      COMPONENT_TYPE_PHONE,
      COMPONENT_TYPES,
    }
  },
  mounted() {
    this.valModel = this.renderEditing(this.val)
    this.editing = this.editMode
  },
  methods: {
    enterEditMode() {
      this.revertValModel = this.valModel
      this.editing = true
    },
    cancelEditMode() {
      this.valModel = this.revertValModel
      this.editing = false
    },
    async persistWrapper() {
      if (this.$refs.editingState.validate()) {
        this.loading = true
        await this.persist(this.valModel)
        this.loading = false
        this.editing = false
      }
    },
  },
  watch: {
    val(newValue) {
      if (!this.editing) {
        this.valModel = this.renderEditing(newValue)
      }
    },
  }
}
</script>

<style lang="sass" scoped>
::v-deep .switch-component .v-label
  justify-content: flex-end
  margin-right: 5%

::v-deep .switch-component .v-input__slot
  flex-direction: row-reverse

::v-deep .inline-textfield-formatted div
  display: inline !important

::v-deep .inline-wrapper
  border: 1px solid #e0e0e0 !important
  box-sizing: border-box
</style>